import api from './api';
import { Article, CreateArticleData, UpdateArticleData } from '../types/article.types';
import { ApiResponse, PaginatedResponse } from '../types/common.types';

interface GetArticlesParams {
  page?: number;
  limit?: number;
  category?: string;
  status?: string;
  search?: string;
  sortBy?: string;
  order?: 'asc' | 'desc';
}

class ArticleService {
  /**
   * Get all articles with filters and pagination
   */
  async getArticles(params: GetArticlesParams = {}): Promise<PaginatedResponse<{ articles: Article[] }>> {
    try {
      const queryParams = new URLSearchParams();

      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.category) queryParams.append('category', params.category);
      if (params.status) queryParams.append('status', params.status);
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.order) queryParams.append('order', params.order);

      console.log('🔍 Fetching articles with params:', Object.fromEntries(queryParams));

      const response = await api.get<PaginatedResponse<{ articles: Article[] }>>(
        `/articles?${queryParams.toString()}`
      );

      console.log(`✅ Fetched ${response.data.data?.articles?.length || 0} articles`);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to fetch articles:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Get single article by slug
   */
  async getArticleBySlug(slug: string): Promise<Article> {
    try {
      console.log('🔍 Fetching article by slug:', slug);

      const response = await api.get<ApiResponse<{ article: Article }>>(`/articles/${slug}`);

      console.log('✅ Article fetched successfully:', response.data.data?.article?.headline);
      return response.data.data!.article;
    } catch (error: any) {
      console.error('❌ Failed to fetch article by slug:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Create new article with image upload
   */
  async createArticle(data: CreateArticleData): Promise<Article> {
    try {
      console.log('📝 Creating article:', data.headline);

      const formData = new FormData();

      // Required fields
      formData.append('headline', data.headline.trim());
      formData.append('description', data.description);
      formData.append('category', data.category);
      formData.append('status', data.status || 'draft');

      // Optional fields
      if (data.excerpt && data.excerpt.trim()) {
        formData.append('excerpt', data.excerpt.trim());
      } else {
        // Auto-generate excerpt from headline
        formData.append('excerpt', data.headline.substring(0, 150));
      }

      // Boolean flags
      formData.append('isFeatured', String(data.isFeatured || false));
      formData.append('isBreakingNews', String(data.isBreakingNews || false));

      // Tags as JSON array
      if (data.tags && data.tags.length > 0) {
        const cleanTags = data.tags.filter(tag => tag.trim());
        if (cleanTags.length > 0) {
          formData.append('tags', JSON.stringify(cleanTags));
        }
      }

      // SEO fields
      if (data.metaTitle && data.metaTitle.trim()) {
        formData.append('seo[metaTitle]', data.metaTitle.trim());
      } else {
        formData.append('seo[metaTitle]', data.headline.trim());
      }

      if (data.metaDescription && data.metaDescription.trim()) {
        formData.append('seo[metaDescription]', data.metaDescription.trim());
      } else {
        formData.append('seo[metaDescription]', data.excerpt || data.headline.substring(0, 160));
      }

      // Featured image (required)
      if (data.featuredImage) {
        formData.append('featuredImage', data.featuredImage);
      }

      // Log form data for debugging
      console.log('📤 Sending form data:');
      

      const response = await api.post<ApiResponse<{ article: Article }>>(
        '/articles',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      console.log('✅ Article created successfully:', response.data.data?.article?.slug);
      return response.data.data!.article;
    } catch (error: any) {
      console.error('❌ Failed to create article:', error.response?.data || error.message);
      
      // Enhanced error logging
      if (error.response?.data?.errors) {
        console.error('Validation errors:', error.response.data.errors);
      }
      
      throw error;
    }
  }

  /**
   * Update existing article by slug
   */
async updateArticle(slug: string, data: UpdateArticleData): Promise<Article> {
    try {
      console.log('💾 Updating article:', slug);

      const formData = new FormData();

      // Add only changed fields
      if (data.headline && data.headline.trim()) {
        formData.append('headline', data.headline.trim());
      }

      if (data.excerpt !== undefined) {
        formData.append('excerpt', data.excerpt.trim());
      }

      if (data.description) {
        formData.append('description', data.description);
      }

      if (data.category) {
        formData.append('category', data.category);
      }

      if (data.status) {
        formData.append('status', data.status);
      }

      // Boolean flags
      if (data.isFeatured !== undefined) {
        formData.append('isFeatured', String(data.isFeatured));
      }

      if (data.isBreakingNews !== undefined) {
        formData.append('isBreakingNews', String(data.isBreakingNews));
      }

      // Tags
      if (data.tags && data.tags.length > 0) {
        const cleanTags = data.tags.filter(tag => tag.trim());
        if (cleanTags.length > 0) {
          formData.append('tags', JSON.stringify(cleanTags));
        }
      }

      // SEO fields
      if (data.metaTitle) {
        formData.append('seo[metaTitle]', data.metaTitle.trim());
      }

      if (data.metaDescription) {
        formData.append('seo[metaDescription]', data.metaDescription.trim());
      }

      // New featured image (optional)
      if (data.featuredImage) {
        formData.append('featuredImage', data.featuredImage);
        console.log('   📷 New featured image attached');
      }

      // Log form data
      console.log('📤 Sending update data:');
    

      const response = await api.put<ApiResponse<{ article: Article }>>(
        `/articles/${slug}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      console.log('✅ Article updated successfully:', response.data.data?.article?.slug);
      return response.data.data!.article;
    } catch (error: any) {
      console.error('❌ Failed to update article:', error.response?.data || error.message);
      
      if (error.response?.data?.errors) {
        console.error('Validation errors:', error.response.data.errors);
      }
      
      throw error;
    }
  }



  /**
   * Delete article by slug
   */
    async deleteArticle(id: string): Promise<void> {
    try {
      console.log('🗑️ Deleting article with ID:', id);

      await api.delete(`/articles/${id}`);

      console.log('✅ Article deleted successfully');
    } catch (error: any) {
      console.error('❌ Failed to delete article:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Like an article
   */
  async likeArticle(id: string): Promise<Article> {
    try {
      console.log('❤️ Liking article with ID:', id);

      const response = await api.post<ApiResponse<{ article: Article }>>(`/articles/${id}/like`);

      console.log('✅ Article liked');
      return response.data.data!.article;
    } catch (error: any) {
      console.error('❌ Failed to like article:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Increment article views
   */
  async incrementViews(slug: string): Promise<void> {
    try {
      await api.post(`/articles/${slug}/view`);
      console.log('👁️ View count incremented');
    } catch (error) {
      // Non-critical error, just log
      console.log('ℹ️ View increment skipped:', error);
    }
  }
}

export default new ArticleService();